import { HubConnectionState } from '@microsoft/signalr';
import { useEffect, useRef, useState } from 'react';
import { useSignalR } from '../contexts';
import { getDebugLogger } from '../utils/debugLogger';

const log = getDebugLogger('signalr:subscription');

export interface BoardSignalRNotification {
  boardId: string;
  eventType: string;
  notification: unknown;
}

export interface UseBoardSignalRSubscriptionArgs {
  boardId?: string;
  enabled?: boolean;
  onNotification?: (notification: BoardSignalRNotification) => void;
  /**
   * Called when the board group is joined again after the connection was
   * lost. Anything broadcast while it was down never arrived, so this is the
   * cue to refetch. Not called for the first join of a board.
   */
  onResubscribed?: () => void;
}

export interface UseBoardSignalRSubscriptionResult {
  connectionId: string | null;
  isSubscribed: boolean;
}

const BROADCAST_NOTIFICATION_METHOD = 'BroadcastNotification';
const ADD_TO_GROUP_METHOD = 'AddToBoardGroup';
const REMOVE_FROM_GROUP_METHOD = 'RemoveFromBoardGroup';

const invokeWithoutThrowing = (
  invoke: (methodName: string, ...args: unknown[]) => unknown,
  methodName: string,
  ...args: unknown[]
): void => {
  try {
    void Promise.resolve(invoke(methodName, ...args)).catch(() => undefined);
  } catch {
    // Best-effort cleanup/join should never throw from hook lifecycle paths.
  }
};

export const useBoardSignalRSubscription = ({
  boardId,
  enabled = true,
  onNotification,
  onResubscribed,
}: UseBoardSignalRSubscriptionArgs): UseBoardSignalRSubscriptionResult => {
  const { connection, isConnected } = useSignalR();
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  // Survives a dropped connection: the cleanup below can only leave the group
  // while connected, so after a drop this still names the board, which is how
  // the next join knows it is a rejoin.
  const joinedBoardIdRef = useRef<string | null>(null);
  const onNotificationRef = useRef(onNotification);
  const onResubscribedRef = useRef(onResubscribed);

  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    onResubscribedRef.current = onResubscribed;
  }, [onResubscribed]);

  useEffect(() => {
    if (!connection || !enabled) {
      return;
    }

    const handleNotification = (
      incomingBoardId: string,
      eventType: string,
      notification: unknown,
    ): void => {
      if (!boardId || incomingBoardId !== boardId) {
        log('Ignoring notification for other board', {
          incomingBoardId,
          boardId,
          eventType,
        });
        return;
      }

      log('Notification received', { boardId: incomingBoardId, eventType });

      onNotificationRef.current?.({
        boardId: incomingBoardId,
        eventType,
        notification,
      });
    };

    connection.on(BROADCAST_NOTIFICATION_METHOD, handleNotification);

    return () => {
      connection.off(BROADCAST_NOTIFICATION_METHOD, handleNotification);
    };
  }, [boardId, connection, enabled]);

  useEffect(() => {
    if (!enabled || !connection || !isConnected || !boardId) {
      log('Not joining board group', {
        boardId,
        enabled,
        hasConnection: !!connection,
        isConnected,
      });
      setIsSubscribed(false);
      setConnectionId(null);

      return;
    }

    let isDisposed = false;

    const joinBoardGroup = async (): Promise<void> => {
      try {
        if (
          joinedBoardIdRef.current &&
          joinedBoardIdRef.current !== boardId &&
          connection.state === HubConnectionState.Connected
        ) {
          log('Leaving previous board group', {
            previousBoardId: joinedBoardIdRef.current,
          });
          await connection.invoke(
            REMOVE_FROM_GROUP_METHOD,
            joinedBoardIdRef.current,
          );
          joinedBoardIdRef.current = null;
        }

        const isRejoin = joinedBoardIdRef.current === boardId;

        log('Joining board group', { boardId, isRejoin });
        const nextConnectionId = await connection.invoke<string>(
          ADD_TO_GROUP_METHOD,
          boardId,
        );

        if (isDisposed) {
          log('Join completed after unmount, discarding result', { boardId });
          return;
        }

        log('Joined board group', { boardId, connectionId: nextConnectionId });
        joinedBoardIdRef.current = boardId;
        setConnectionId(nextConnectionId);
        setIsSubscribed(true);

        if (isRejoin) {
          onResubscribedRef.current?.();
        }
      } catch (err) {
        if (isDisposed) {
          return;
        }

        log('Failed to join board group', { boardId, err });
        console.error("[SignalR] Failed to join board group:", boardId, err);
        setIsSubscribed(false);
        setConnectionId(null);
      }
    };

    void joinBoardGroup();

    return () => {
      isDisposed = true;
      setIsSubscribed(false);

      if (
        joinedBoardIdRef.current === boardId &&
        connection.state === HubConnectionState.Connected
      ) {
        log('Leaving board group on cleanup', { boardId });
        invokeWithoutThrowing(connection.invoke.bind(connection), REMOVE_FROM_GROUP_METHOD, boardId);
        joinedBoardIdRef.current = null;
      }
    };
  }, [boardId, connection, enabled, isConnected]);

  return {
    connectionId,
    isSubscribed,
  };
};